import React, { useCallback, useEffect, useMemo } from "react";

import {
  Menu,
  MenuPopover,
  MenuTrigger,
  SplitButton,
  Tooltip,
  Button,
  mergeClasses,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  Body1,
  Image
} from "@fluentui/react-components";
import {
  ChevronUpFilled,
  Video24Filled,
  VideoOff24Filled,
  Dismiss24Regular,
  Settings20Filled,
  InfoRegular
} from "@fluentui/react-icons";
import { useShare } from "../room/useShare";
import { useCameraDevices } from "../room/useCameraDevices";
import { MicPopoverSelect } from "../fluent/MicPopoverSelect";
import configManager from "../../singleton/ConfigManager";
import PropTypes from "prop-types";
import { useSettingsContext } from "../room/SettingsContext";
import { NO_DEVICE_ID, PermissionStatus } from "../../utils/media-devices-utils";
import PermissionDenied from "../../assets/images/PermissionDenied.png";

export default function ToolbarVideo({ styles, scene, hubChannel }) {
  const mediaDeiceManager = APP.mediaDevicesManager;

  const { sharingSource, canShareCamera, toggleShareCamera } = useShare(scene, hubChannel);

  const { videoDeviceChanged, videoDevices, fetchVideoDevices, getVideoDevices, permissionStatusVideo } =
    useCameraDevices();
  const { onOpenChange: onOpenSettingsChange, onTabChange: onTabSettingsChange } = useSettingsContext();

  const videoPermissionDenied = permissionStatusVideo === PermissionStatus.DENIED;

  const name = "video";

  const { value, options } = videoDevices;

  const isNotConfigured = useMemo(
    () => options.length === 0 || (options.length && options[0].value === NO_DEVICE_ID),
    [options]
  );

  useEffect(() => {
    if (configManager.config.spawnCamera?.enabled && isNotConfigured) {
      getVideoDevices();
    }
  }, [permissionStatusVideo, isNotConfigured, getVideoDevices]);

  const [checkedValues, setCheckedValues] = React.useState({ [name]: [value] });
  React.useEffect(() => {
    setCheckedValues({ [name]: [value] });
  }, [value]);

  const onChangeChecked = (e, { name, checkedItems }) => {
    setCheckedValues(s => ({ ...s, [name]: checkedItems }));
  };

  const onClick = () => toggleShareCamera();

  const primaryActionButtonProps = {
    onClick
  };

  const isStreamingCamera = sharingSource && sharingSource === "camera";
  const disableAndShowTooltip = sharingSource && sharingSource !== "camera";
  const [visible, setVisible] = React.useState(false);

  const onOpenSettings = () => {
    onOpenSettingsChange(true);
    onTabSettingsChange("audiovideo");
  };

  const handleClick = useCallback(() => {
    if (
      configManager.config.spawnCamera?.enabled &&
      isNotConfigured &&
      permissionStatusVideo === PermissionStatus.PROMPT
    ) {
      fetchVideoDevices();
    }
  }, [fetchVideoDevices, isNotConfigured, permissionStatusVideo]);

  if (videoPermissionDenied) {
    return (
      <Dialog>
        <DialogTrigger disableButtonEnhancement>
          {triggerProps => (
            <SplitButton
              primaryActionButton={primaryActionButtonProps}
              menuButton={triggerProps}
              appearance="subtle"
              className={mergeClasses(styles.buttonSplit, styles.buttonSplitDisabled)}
              icon={<VideoOff24Filled style={{ width: "24px", height: "24px", color: "#EB5757" }} />}
              menuIcon={<InfoRegular style={{ width: "16px", height: "16px", marginBottom: "4px", color: "#fff" }} />}
            />
          )}
        </DialogTrigger>
        <DialogSurface style={{ maxWidth: "480px" }}>
          <DialogBody>
            <DialogTitle
              action={
                <DialogTrigger>
                  <Button appearance="transparent" icon={<Dismiss24Regular />} />
                </DialogTrigger>
              }
            >
              The metaverse is blocked from using your camera
            </DialogTitle>
            <DialogContent style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <Body1 style={{ color: "#bdbdbd" }}>1. Click the page info icon on your browser's address bar.</Body1>
                <Body1 style={{ color: "#bdbdbd" }}>2. Turn on the camera permissions.</Body1>
              </div>
              <Image src={PermissionDenied} style={{ borderRadius: "8px" }} />
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    );
  }

  return (
    <Menu positioning="below" checkedValues={checkedValues} onCheckedValueChange={onChangeChecked}>
      <MenuTrigger disableButtonEnhancement>
        {triggerProps => (
          <Tooltip
            visible={visible && disableAndShowTooltip}
            onVisibleChange={(_ev, data) => setVisible(data.visible)}
            content="Camera sharing is not available during screen sharing"
          >
            <SplitButton
              disabled={disableAndShowTooltip}
              primaryActionButton={primaryActionButtonProps}
              menuButton={triggerProps}
              appearance="subtle"
              className={
                disableAndShowTooltip
                  ? mergeClasses(styles.buttonSplit, styles.buttonSplitDisabled)
                  : styles.buttonSplit
              }
              icon={
                canShareCamera && !disableAndShowTooltip && isStreamingCamera ? (
                  <Video24Filled
                    className={disableAndShowTooltip ? styles.iconDisabled : ""}
                    style={{ width: "24px", height: "24px" }}
                  />
                ) : (
                  <VideoOff24Filled
                    className={disableAndShowTooltip ? styles.iconDisabled : ""}
                    style={{
                      width: "24px",
                      height: "24px",
                      color: canShareCamera && !isStreamingCamera ? "#EB5757" : "inherit"
                    }}
                  />
                )
              }
              menuIcon={
                <ChevronUpFilled style={{ width: "16px", height: "16px", marginBottom: "4px", color: "#fff" }} />
              }
              onClick={handleClick}
            />
          </Tooltip>
        )}
      </MenuTrigger>
      <MenuPopover
        style={{ minWidth: "450px", display: "flex", gap: "10px", alignItems: "center" }}
        className={styles.menuPopover}
      >
        <MicPopoverSelect
          popoverClassName={styles.micPopoverContainerVideo}
          positioning="above-start"
          name={"video"}
          Icon={Video24Filled}
          {...videoDevices}
          value={mediaDeiceManager.selectedVideoDeviceId}
          onChange={videoDeviceChanged}
        ></MicPopoverSelect>
        <Button onClick={onOpenSettings} appearance="transparent" icon={<Settings20Filled />} />
      </MenuPopover>
    </Menu>
  );
}

ToolbarVideo.propTypes = {
  styles: PropTypes.object,
  scene: PropTypes.object.isRequired,
  hubChannel: PropTypes.object.isRequired
};
